/*
  Keeps the header cart badge in sync with the real cart.

  Two separate problems this solves:

  1. Stale on load. The badge is `{{ cart.item_count }}` rendered by Liquid, and
     Shopify's CDN serves guests a full-page-cached copy of the page that was
     rendered with an empty cart — so the badge reads 0 no matter what is in the
     cart. We hydrate it from /cart.js once the page is up.

  2. Never updates. Nothing in the theme wrote to `.count` at all. The cart
     drawer app handles add/remove itself and only repaints its own DOM, so the
     badge kept whatever the server sent. We watch for any /cart/* mutation —
     fetch or XHR, whoever made it — and repaint.

  Anything that should show the count carries `data-cart-count`. The `.hcart
  .count` / `.fixed-mobile-cart .count` selectors are kept as a fallback so a
  markup spot that was missed still works.
*/
(function () {
  'use strict';

  if (window.__grCartCount) return;

  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;
  var COUNT_SELECTOR = '[data-cart-count], .hcart .count, .fixed-mobile-cart .count';

  var timer = null;
  var inflight = false;
  var queued = false;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function paint(count) {
    var text = String(count);
    var nodes = document.querySelectorAll(COUNT_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.textContent.trim() !== text) el.textContent = text;
      el.classList.toggle('cart-count--empty', count === 0);
    }
    document.documentElement.classList.toggle('cart-is-empty', count === 0);
  }

  function sync() {
    if (inflight) { queued = true; return; }
    inflight = true;

    fetch(root() + 'cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
      cache: 'no-store'
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart && typeof cart.item_count === 'number') paint(cart.item_count);
      })
      .catch(function () { /* offline or blocked; leave the badge alone */ })
      .then(function () {
        inflight = false;
        if (queued) { queued = false; schedule(150); }
      });
  }

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(sync, delay == null ? 80 : delay);
  }

  /* ---------- catch cart mutations, whoever makes them ---------- */

  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input) {
      var url = (input && input.url) ? input.url : String(input);
      var result = nativeFetch.apply(this, arguments);
      if (CART_MUTATION_RE.test(url)) {
        result.then(function () { schedule(120); }).catch(function () {});
      }
      return result;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__grCartUrl = url;
    return nativeOpen.apply(this, arguments);
  };

  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (CART_MUTATION_RE.test(this.__grCartUrl || '')) {
      this.addEventListener('load', function () { schedule(120); });
    }
    return nativeSend.apply(this, arguments);
  };

  /* Safety net for apps that bypass both (their own transport, or they wrapped
     fetch before we did). A click on any add-to-cart control gets two cheap
     follow-up reads. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('[name="add"], .product-form__submit, .add-cart-btn, .gr-duo__atc, [data-add-to-cart]')) {
      schedule(900);
      setTimeout(sync, 2200);
    }
  }, true);

  /* Events the theme's own sections already fire. */
  document.addEventListener('fsb:cart-changed', function () { schedule(120); });
  document.addEventListener('cart:refresh', function () { schedule(120); });

  /* ---------- hydrate ---------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { sync(); });
  } else {
    sync();
  }

  // Back/forward cache restore hands back the old DOM with the old number.
  window.addEventListener('pageshow', function (e) { if (e.persisted) sync(); });

  // Returning from checkout, or from another tab that changed the cart.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') schedule(200);
  });

  window.__grCartCount = { sync: sync, schedule: schedule, paint: paint };
})();
